// Shared request-id generation for BOTH HTTP clients — the fetch client
// (services/api/client.ts) and the axios client (services/api.ts). Every
// outbound request gets a fresh, unique id sent as the `X-Request-ID`
// header, letting the Go backend (which honors an inbound X-Request-ID via
// chi's middleware.RequestID — see internal/api/middleware.WithContextFields,
// which pulls it back out via middleware.GetReqID) correlate its
// request-scoped logs with the exact browser request that triggered them.
//
// Prior to this, the SPA sent no X-Request-ID at all, so every backend log
// line's request_id was a locally-generated chi id with no way to trace it
// back to a specific frontend action.

export const REQUEST_ID_HEADER = 'X-Request-ID'

/**
 * generateRequestId returns a fresh unique id for one outbound request.
 * Prefers crypto.randomUUID() (available in all supported browsers and in
 * Node >=19 for Vitest); falls back to a timestamp+random string for older
 * runtimes or test environments where crypto.randomUUID is unavailable.
 */
export function generateRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to the fallback below
  }
  const random = Math.random().toString(36).slice(2)
  return `${Date.now().toString(36)}-${random}`
}
